/**
 * PNG to SVG Conversion Service
 * Uses imagetracerjs for proper vectorization
 */

// @ts-expect-error - imagetracerjs doesn't have type definitions
import ImageTracer from 'imagetracerjs';

export type ConversionMode = 'embed' | 'trace';
export type TracePreset = 'default' | 'posterized' | 'curvy' | 'sharp' | 'detailed' | 'smoothed' | 'grayscale' | 'fixedpalette' | 'randomsampling' | 'artistic';

interface ConvertOptions {
  mode?: ConversionMode;
  preset?: TracePreset;
  colors?: number;
}

/**
 * Convert PNG image to SVG
 * - 'embed' mode: Embeds the image as base64 (perfect quality)
 * - 'trace' mode: Uses imagetracerjs for proper vectorization
 */
export async function convertPngToSvg(
  file: File,
  options: ConvertOptions = {}
): Promise<string> {
  const { mode = 'embed' } = options;

  if (mode === 'embed') {
    return embedImageAsSvg(file);
  } else {
    return traceImageWithLibrary(file, options);
  }
}

/**
 * Embed image as base64 inside SVG
 * This preserves perfect quality
 */
async function embedImageAsSvg(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      const img = new Image();
      
      img.onload = () => {
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${img.width} ${img.height}" width="${img.width}" height="${img.height}">
  <image width="${img.width}" height="${img.height}" xlink:href="${dataUrl}"/>
</svg>`;
        resolve(svg);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataUrl;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Trace image using imagetracerjs library
 */
async function traceImageWithLibrary(file: File, options: ConvertOptions): Promise<string> {
  const { preset = 'default', colors = 16 } = options;
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        try {
          // Create canvas to get image data
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('Canvas not supported'));
            return;
          }

          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          
          const imageData = ctx.getImageData(0, 0, img.width, img.height);
          
          // Get preset options from imagetracerjs
          const traceOptions = getTraceOptions(preset, colors);
          
          // Trace the image
          const svgString = ImageTracer.imagedataToSVG(imageData, traceOptions);
          
          resolve(svgString);
        } catch (err) {
          reject(new Error('Failed to trace image'));
        }
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Get trace options based on preset
 */
function getTraceOptions(preset: TracePreset, colors: number): object {
  // Base options that can be customized
  const baseOptions = {
    numberofcolors: colors,
  };
  
  // imagetracerjs has built-in presets
  switch (preset) {
    case 'posterized':
      return { ...ImageTracer.optionpresets.posterized1, ...baseOptions };
    case 'curvy':
      return { ...ImageTracer.optionpresets.curvy, ...baseOptions };
    case 'sharp':
      return { ...ImageTracer.optionpresets.sharp, ...baseOptions };
    case 'detailed':
      return { ...ImageTracer.optionpresets.detailed, ...baseOptions };
    case 'smoothed':
      return { ...ImageTracer.optionpresets.smoothed, ...baseOptions };
    case 'grayscale':
      return { ...ImageTracer.optionpresets.grayscale, ...baseOptions };
    case 'fixedpalette':
      return { ...ImageTracer.optionpresets.fixedpalette, ...baseOptions };
    case 'randomsampling':
      return { ...ImageTracer.optionpresets.randomsampling1, ...baseOptions };
    case 'artistic':
      return { 
        ...ImageTracer.optionpresets.artistic1, 
        ...baseOptions 
      };
    case 'default':
    default:
      return { 
        ...ImageTracer.optionpresets.default,
        ...baseOptions 
      };
  }
}
